/**
 * What Core and each paid module actually contain, in the buyer's language.
 *
 * This is marketing copy, and it is here rather than inside a page because it is
 * read by three surfaces that must not disagree: the public pricing page, the
 * signup module picker, and the in-app upsell. The public pricing page previously
 * carried its own hardcoded list headed "Everything included on every plan", which
 * stayed true for exactly as long as it took to ship modules.
 *
 * Prices and quota allowances are deliberately NOT here — those come from
 * /api/stripe/plans, which reads billing.py and the Stripe price book. A number
 * written into a frontend file is a number that will disagree with Stripe within a
 * quarter; the same page is the proof, having advertised $129/$299/$649 for months
 * after the real prices became $79/$179/$399.
 *
 * Grouping mirrors MODULE_ROUTE_PREFIXES in moduleMetadata.ts — anything not routed
 * to a module is Core. Keep them in step: a feature listed here under a module it
 * is not actually gated by is a promise the product will not keep.
 */
import { ModuleKey } from "./moduleMetadata";

/** One line each — what the module does, not what it contains. */
export const MODULE_PITCH: Record<ModuleKey, string> = {
  recruitment:        "Job posts, CV screening and interview scheduling",
  finance:            "Invoicing, quotations, vendor bills and Thai tax docs",
  payroll:            "Salary runs, payslips and statutory deductions",
  esign:              "Send documents for legally-binding signature",
  ai_expense_capture: "Photograph a receipt, get a filed expense",
  performance:        "Goals, reviews and 360 feedback",
};

/**
 * Core — the HRIS every plan includes, with no module purchase and no gate.
 *
 * Attendance and Timesheet are Core by decision, not oversight (they were moved
 * out of a module in PR #326). An employee reading their own payslip is Core too:
 * /my-slips stays available whether or not the workspace bought Payroll.
 */
export const CORE_FEATURES: string[] = [
  "Employee profiles, directory & org chart",
  "Bulk employee import via CSV",
  "Leave management, balances & carry-forward",
  "Attendance — check-in/out, WFH & regularizations",
  "Timesheets & project time tracking",
  "Expense claims with receipt upload & approval",
  "Assets register — assign & track equipment",
  "Exit & offboarding clearance checklists",
  "HR letters — 11 types, AI-drafted (offer, experience, increment…)",
  "Company policy upload & AI-powered search (OCR)",
  "Aria — the AI HR assistant, 24/7 and context-aware",
  "Announcements, helpdesk, referrals & pulse surveys",
  "Reports — headcount, leave utilization, payroll cost",
  "Employees view their own payslips",
  "TOTP & passkey (WebAuthn) authentication",
  "Role-based access — 7 roles",
];

/** What each add-on module adds on top of Core. */
export const MODULE_FEATURES: Record<ModuleKey, string[]> = {
  recruitment: [
    "Jobs, candidate pipeline & interview scheduling",
    "AI CV scoring & shortlisting",
    "AI video interview screening — every answer scored",
    "Talent pool with re-ranking against new roles",
    "Public careers page with apply form (PDPA-compliant)",
    "Offer letters — AI-drafted, e-signable PDF",
    "LinkedIn job posting",
  ],
  finance: [
    "Invoices, quotations & client management",
    "Tax invoices, receipts & credit/debit notes",
    "Vendors, vendor bills & recurring bills",
    "Product & service catalogue",
    "Thai tax documents — PP30, WHT certificates",
    "AI vendor-bill reading",
  ],
  payroll: [
    "Salary runs & payslips (PDF, bulk generate)",
    "Thai & Indian statutory compliance (SSF, PVD, PF, ESI, PT, Gratuity)",
    "PND.1, SSO.10 & Form 16 exports",
    "Salary structures, allowances & deductions",
  ],
  esign: [
    "Send any document for legally-binding signature",
    "Sequential & parallel signer routing",
    "Certificate of completion with full audit trail",
    "Automatic reminders & expiry",
  ],
  ai_expense_capture: [
    "Photograph a receipt, get a filed expense",
    "AI line-item, VAT & WHT extraction",
    "Bulk receipt upload & review dashboard",
    "Approve straight into Finance",
  ],
  performance: [
    "Review cycles — self, manager & 360° peer",
    "Goals & OKR tracking",
    "Upward feedback",
    "Competency frameworks & rating scales",
  ],
};
